import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { AlertTriangle, CalendarClock, MessageCircle, UserPlus, IndianRupee, Wrench } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT, Bi } from "@/lib/i18n";
import { inr, monthLabel, dateLabel, waLink, reminderText } from "@/lib/format";
import { Card, ErrorState, ListSkeleton, PageTitle } from "@/components/common";

function Stat({ label, value, sub, tone, testId }: { label: React.ReactNode; value: string; sub?: string; tone: string; testId: string }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-extrabold font-mono-num ${tone}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </Card>
  );
}

function CollectionCard({ d }: { d: any }) {
  const { t } = useT();
  const pct = d.due ? Math.round((d.collected * 100) / d.due) : 0;
  return (
    <Card className="p-5 bg-emerald-900 border-emerald-900 text-white" data-testid="dashboard-collection-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-200">{t("this_month")} · {t("collected")}</p>
      <p className="mt-2 text-4xl font-extrabold font-mono-num" data-testid="dashboard-collected">{inr(d.collected)}</p>
      <p className="text-sm text-emerald-200 mt-1">{t("of_due")} {inr(d.due)}</p>
      <div className="mt-4 h-2.5 rounded-full bg-emerald-800 overflow-hidden">
        <div className="h-full bg-[#fccc00] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-xs text-emerald-200">
        <span>{pct}%</span>
        <span data-testid="dashboard-pending">{t("pending")}: {inr(d.pending)}</span>
      </div>
    </Card>
  );
}

function Chart({ data }: { data: any[] }) {
  const { t, lang } = useT();
  const rows = data.map((c) => ({ ...c, label: monthLabel(c.month, lang).split(" ")[0] }));
  return (
    <Card className="p-4" data-testid="dashboard-chart">
      <p className="text-sm font-bold mb-3"><Bi k="six_month_chart" /></p>
      <div className="h-44">
        <ResponsiveContainer width="100%" height={176} minWidth={200}>
          <BarChart data={rows} barGap={2}>
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
            <Tooltip formatter={(v: any) => inr(v)} cursor={{ fill: "#f1f5f9" }} />
            <Bar dataKey="due" name={t("due")} fill="#e2e8f0" radius={[6, 6, 0, 0]} />
            <Bar dataKey="collected" name={t("collected")} fill="#047857" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function OverdueList({ items, pg }: { items: any[]; pg: string }) {
  const { t, lang } = useT();
  return (
    <Card className="p-4" data-testid="dashboard-overdue-list">
      <p className="text-sm font-bold mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-600" /><Bi k="overdue_list" /></p>
      {items.length === 0 ? <p className="text-sm text-green-700" data-testid="dashboard-no-overdue">{t("no_overdue")}</p> : (
        <ul className="divide-y divide-slate-100">
          {items.map((o) => (
            <li key={o.record_id} className="py-3 flex items-center justify-between gap-2">
              <Link to={`/tenants/${o.tenant_id}`} className="min-w-0" data-testid={`overdue-tenant-${o.record_id}`}>
                <p className="font-semibold truncate">{o.tenant_name}</p>
                <p className="text-xs text-slate-500">{monthLabel(o.month, lang)} · <span className="text-rose-600 font-semibold font-mono-num">{inr(o.outstanding)}</span></p>
              </Link>
              {o.tenant_phone && (
                <a data-testid={`overdue-remind-${o.record_id}`} href={waLink(o.tenant_phone, reminderText(o.tenant_name, o.outstanding, o.month, pg))} target="_blank" rel="noreferrer"
                  className="h-11 px-4 rounded-full bg-[#25D366]/10 text-[#128C4A] text-sm font-semibold flex items-center gap-1.5 pg-tap shrink-0">
                  <MessageCircle className="h-4 w-4" />{t("remind")}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MoveOuts({ items }: { items: any[] }) {
  const { t, lang } = useT();
  return (
    <Card className="p-4" data-testid="dashboard-moveouts">
      <p className="text-sm font-bold mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-indigo-600" /><Bi k="upcoming_moveouts" /></p>
      {items.length === 0 ? <p className="text-sm text-slate-500">{t("no_moveouts")}</p> : items.map((m) => (
        <Link key={m.id} to={`/tenants/${m.id}`} data-testid={`moveout-${m.id}`} className="flex justify-between py-2 text-sm">
          <span className="font-semibold">{m.name}</span><span className="text-slate-500">{dateLabel(m.expected_move_out, lang)}</span>
        </Link>
      ))}
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useT();
  const nav = useNavigate();
  const { property, owner } = useAuth();
  const { data: d, loading, error, reload } = useApi<any>(property ? `/dashboard?property_id=${property.id}` : null);
  return (
    <div>
      <PageTitle title={`${t("hello")}${owner?.name ? ", " + owner.name : ""}`} sub={property?.name} />
      {loading && !d ? <ListSkeleton rows={5} /> : error ? <ErrorState message={error} onRetry={reload} /> : d && (
        <div className="space-y-4 pg-stagger">
          <CollectionCard d={d} />
          <div className="grid grid-cols-2 gap-3">
            <Stat testId="dashboard-occupancy" label={<Bi k="occupancy" />} value={`${d.occupancy_pct}%`} sub={`${d.beds_occupied}/${d.beds_total} ${t("beds_filled")}`} tone="text-slate-900" />
            <Stat testId="dashboard-overdue" label={<Bi k="overdue" />} value={inr(d.overdue_amount)} sub={`${d.overdue_count} · ${t("open_complaints")}: ${d.open_complaints}`} tone={d.overdue_count ? "text-rose-600" : "text-green-700"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="dashboard-add-tenant-btn" onClick={() => nav("/beds")} className="h-14 rounded-2xl bg-white border border-slate-200 font-semibold flex items-center justify-center gap-2 pg-tap"><UserPlus className="h-5 w-5 text-emerald-700" />{t("quick_add_tenant")}</button>
            <button data-testid="dashboard-collect-btn" onClick={() => nav("/rent")} className="h-14 rounded-2xl bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2 pg-tap"><IndianRupee className="h-5 w-5" />{t("quick_collect")}</button>
          </div>
          <OverdueList items={d.overdue} pg={property.name} />
          <Chart data={d.chart} />
          <MoveOuts items={d.upcoming_moveouts} />
          {d.beds_maintenance > 0 && <p className="text-xs text-slate-500 flex items-center gap-1"><Wrench className="h-3.5 w-3.5" />{d.beds_maintenance} {t("bed_maintenance")}</p>}
        </div>
      )}
    </div>
  );
}
